import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import PartnerBadges, { RosetteIcon } from '../PartnerBadges';
import { isPartnerClickable, PARTNER_DISABLED_TOOLTIP } from '../../lib/partnerPolicy';

/**
 * PartnerCardsGridBlock - Grille de cartes partenaires
 * 
 * Features :
 * ✅ 3 cartes par ligne (desktop), 1 par ligne (mobile)
 * ✅ Récupération automatique depuis API /api/partners
 * ✅ Filtre partenaires inactifs automatique
 * ✅ Carte entièrement cliquable (si link)
 * ✅ Animation hover
 * ✅ Lazy loading images
 * ✅ Responsive
 * 
 * Mode d'affichage :
 * 1. Si block.content.cards existe et n'est pas vide → Affiche les cartes configurées (mode manuel)
 * 2. Sinon → Récupère automatiquement TOUS les partenaires actifs depuis /api/partners (mode auto)
 * 
 * Structure content :
 * {
 *   cards: [], // Optionnel - si vide, récupère depuis API
 *   corner_radius: 'sm' | 'md' | 'lg' | 'xl',
 *   card_spacing: 'sm' | 'md' | 'lg'
 * }
 */
const PartnerCardsGridBlock = ({ block }) => {
  const {
    cards: manualCards = [],
    corner_radius = 'lg',
    card_spacing = 'md'
  } = block.content || {};

  const [apiPartners, setApiPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  // Récupérer les partenaires depuis l'API si pas de cartes manuelles
  useEffect(() => {
    const fetchPartners = async () => {
      // Si des cartes manuelles existent, ne pas faire d'appel API
      if (manualCards && manualCards.length > 0) {
        console.log("🔍 [PartnerCardsGrid] Mode manuel - Cartes configurées:", manualCards.length);
        return;
      }

      try {
        setLoading(true);
        console.log("🔍 [PartnerCardsGrid] Mode auto - Récupération depuis API...");
        
        const response = await api.get('/partners?is_active=true');
        const partnersData = response.data || [];
        
        console.log("📡 [PartnerCardsGrid] API Response:", response);
        console.log("📊 [PartnerCardsGrid] Partners extraits:", partnersData);
        
        // Transformer les partenaires en format carte + tri alphabétique A→Z
        const transformedCards = partnersData
          .slice()
          .sort((a, b) => {
            const nameA = (a.nom || a.name || '').toLowerCase().trim();
            const nameB = (b.nom || b.name || '').toLowerCase().trim();
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
          })
          .map(partner => ({
            id: partner.id,
            title: partner.nom || partner.name || 'Partenaire',
            description: partner.description || '',
            image: partner.logo || partner.banniere || '',
            link: `/partner/${partner.slug || partner.id}`,
            partnerId: partner.id,
            is_active: partner.is_active !== false,
            supplier_type: partner.supplier_type || 'other',
            is_partner: partner.is_partner === true,
            capabilities: partner.capabilities || [],
            has_users: !!partner.has_users,
            order: 0
          }));
        
        console.log("🧩 [PartnerCardsGrid] Cartes transformées (triées A→Z):", transformedCards);
        setApiPartners(transformedCards);
      } catch (error) {
        console.error("❌ [PartnerCardsGrid] Erreur API:", error);
        setApiPartners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [manualCards]);

  // Déterminer quelles cartes afficher (manuelles ou API)
  const cardsToDisplay = manualCards && manualCards.length > 0 ? manualCards : apiPartners;
  
  console.log("🎯 [PartnerCardsGrid] Cartes à afficher:", cardsToDisplay);

  // Filtrer les cartes actives ET dans leur période de visibilité
  const now = new Date();
  const activeCards = cardsToDisplay.filter(card => {
    // 1. Si is_active = false, toujours cacher
    if (card.is_active === false) return false;

    // 2. Vérifier startDate (afficher si now >= startDate)
    if (card.startDate) {
      const startDate = new Date(card.startDate);
      if (now < startDate) return false;
    }

    // 3. Vérifier endDate (afficher si now <= endDate)
    if (card.endDate) {
      const endDate = new Date(card.endDate);
      if (now > endDate) return false;
    }

    // Si toutes les conditions passent, afficher la carte
    return true;
  });

  console.log("✅ [PartnerCardsGrid] Cartes actives après filtrage:", activeCards);

  // Affichage état de chargement
  if (loading) {
    return (
      <div 
        className="partner-cards-grid-block"
        data-testid="partner-cards-grid-block-loading"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        <div className="text-center py-6">
          <p className="text-gray-600">Chargement des partenaires...</p>
        </div>
      </div>
    );
  }

  // Si aucune carte active, ne rien afficher
  if (activeCards.length === 0) {
    console.warn("⚠️ [PartnerCardsGrid] Aucune carte active à afficher");
    return null;
  }

  // Mapping coins arrondis
  const radiusMap = {
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl'
  };
  const radiusClass = radiusMap[corner_radius] || 'rounded-lg';

  // Mapping espacement
  const spacingMap = {
    sm: 'gap-3',
    md: 'gap-6',
    lg: 'gap-8'
  };
  const spacingClass = spacingMap[card_spacing] || 'gap-6';

  return (
    <div 
      className="partner-cards-grid-block"
      data-testid="partner-cards-grid-block"
      data-block-type="partner_cards_grid"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Grid responsive : 1 col mobile, 3 cols desktop */}
      <div className={`grid grid-cols-1 md:grid-cols-3 ${spacingClass}`}>
        {activeCards.map((card) => (
          <PartnerCard
            key={card.id}
            card={card}
            radiusClass={radiusClass}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * PartnerCard - Carte partenaire individuelle
 * 
 * DESIGN: Logo uniformisé avec conteneur fixe
 */
const PartnerCard = ({ card, radiusClass }) => {
  const {
    image,
    title,
    description,
    link,
    startDate,
    endDate,
    supplier_type,
    is_partner,
    capabilities,
    has_users
  } = card;

  // Formater une date en français (sans heure)
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return null;

      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  // Générer le texte des dates
  const getDateText = () => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (start && end) return `Du ${start} au ${end}`;
    if (start) return `À partir du ${start}`;
    if (end) return `Jusqu'au ${end}`;
    return null;
  };

  const dateText = getDateText();
  const isExternal = link && (link.startsWith('http://') || link.startsWith('https://'));
  // ACCESS RULE PARTNER — use shared helper
  const clickable = isPartnerClickable(card);

  // Contenu de la carte
  const cardContent = (
    <div
      className={`card-elevated-v2 ${radiusClass} ${
        clickable 
          ? 'hover:scale-[1.02] cursor-pointer focus-within:ring-2 focus-within:ring-blue-500' 
          : 'opacity-75'
      }`}
      data-testid="partner-card"
    >
      {/* Conteneur Logo/Image - Hauteur fixe uniformisée */}
      <div 
        className="relative overflow-hidden bg-white border-b border-black/[0.06] flex items-center justify-center"
        style={{
          height: '160px',
          minHeight: '160px'
        }}
      >
        {/* Badge partenaire commercial — overlay top-right dans la zone logo */}
        {is_partner === true && (
          <div
            className="absolute top-2 right-2 z-10 drop-shadow-sm"
            title="Partenaire commercial"
            data-testid="partner-card-rosette"
          >
            <RosetteIcon className="w-7 h-7 sm:w-9 sm:h-9" />
          </div>
        )}
        {image ? (
          <div 
            className="w-full h-full flex items-center justify-center p-4"
            style={{
              /* Conteneur centré pour le logo */
              maxWidth: '100%',
              maxHeight: '100%'
            }}
          >
            <img
              src={image}
              alt={title || 'Partenaire'}
              className="max-w-full max-h-full object-contain"
              style={{
                /* Contraintes strictes anti-débordement */
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100% - 32px)',
                maxHeight: 'calc(100% - 32px)'
              }}
              loading="lazy"
              data-testid="partner-card-image"
            />
          </div>
        ) : (
          /* Placeholder si pas d'image */
          <div className="flex items-center justify-center w-full h-full">
            <div className="text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="text-sm">Partenaire</span>
            </div>
          </div>
        )}
      </div>

      {/* Contenu textuel - PARTNER CARDS UI: titre centré + badges */}
      <div className="p-4 flex flex-col items-center justify-center">
        {/* Titre - centré */}
        {title && (
          <h3
            className="text-lg md:text-xl font-heading font-bold text-gray-900 text-center"
            data-testid="partner-card-title"
            title={title}
          >
            {title}
          </h3>
        )}

        {/* Badge type fournisseur uniquement (rosette dans la zone logo) */}
        {supplier_type && (
          <div className="mt-2">
            <PartnerBadges
              supplierType={supplier_type || 'other'}
              capabilities={capabilities}
              isPartner={false}
              size="sm"
              maxBadges={2}
              showTypeLabel={true}
            />
          </div>
        )}

        {/* 🔥 DESCRIPTION MASQUÉE - Partner cards shop view
        {description && (
          <p
            className="text-sm text-gray-600 mb-3 line-clamp-2"
            data-testid="partner-card-description"
            title={description}
          >
            {description}
          </p>
        )}
        */}

        {/* Dates (si existent) - conservées */}
        {dateText && (
          <div className="mt-3 pt-3 border-t border-gray-200 w-full">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
              <span className="text-base">📅</span>
              <span>{dateText}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Si cliquable, wrapper dans Link/a (ACCESS RULE PARTNER)
  if (clickable && link) {
    if (isExternal) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Voir le partenaire : ${title || 'Sans titre'}`}
          className="block focus:outline-none"
        >
          {cardContent}
        </a>
      );
    } else {
      return (
        <Link
          to={link}
          aria-label={`Voir le partenaire : ${title || 'Sans titre'}`}
          className="block focus:outline-none"
        >
          {cardContent}
        </Link>
      );
    }
  }

  // Sinon, rendu normal (non cliquable)
  return cardContent;
};

export default PartnerCardsGridBlock;
